import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { Slide, Fade } from "@mui/material";
import Stack from "@mui/material/Stack";
import Nav from "react-bootstrap/Nav";
import NavDropdown from "react-bootstrap/NavDropdown";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiEdit2 } from "react-icons/fi";
import { LuScanLine } from "react-icons/lu";
import { RiDeleteBin5Line, RiHistoryLine } from "react-icons/ri";

import ListHeading from "../../../common/RDTListHeading/ListHeading";
import SubHeaderComponent from "../../../common/RDTSubHeaderComponent/SubHeaderComponent";
import GeneralButton from "../../../common/SaveButton/GeneralButton";
import CustomModal from "../../../common/CustomModal/CustomModal";
import ShowAlert from "../../../common/ShowAlert/ShowAlert";
import DataTableSkeleton from "../../../common/Skeletons/DataTableSkeleton";
import Images from "../../../../utils/images";
import useWindowDimensions from "../../../../utils/getHeightWidth";
import { changeTitle } from "../../../../state/slices/header";
import GetCapabilities from "../../../../utils/getCapabilities";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { showError, showSuccess, showWarning } from "../../../../utils/showMessage";
import { getScanConfigs, deleteScanConfig, startScan, getScanHistory } from "../../../../api/credentialScanning";
import CredScanBreadcrumb from "../components/CredScanBreadcrumb";
import plus from "../../../../images/plus.svg";
import FirstPageIcon from "../../../../images/first_page.svg";
import LastPageIcon from "../../../../images/last_page.svg";
import NextPageIcon from "../../../../images/arrow_right.svg";
import PreviousPageIcon from "../../../../images/arrow_left.svg";

import "../index.scss";

const pages_url = "dashboard/discovery/hardcoded-credentials/configs";

const ActionComponent = ({ onAdd }) => {
  const { t } = useTranslation();
  const my_capabilities = GetCapabilities();
  const isCapable = my_capabilities[0];
  if (!isCapable?.credential_scanning?.add) return null;
  return (
    <Stack direction="row" spacing={2} className="py-3">
      <GeneralButton
        onClickEvent={onAdd}
        variant="outlined"
        startIcon={<LazyLoadImage src={plus} alt="icon" id="center-blueShadow" />}
        value={t("credentialScanning.newConfiguration")}
        size="large"
      />
    </Stack>
  );
};

const scheduleLabel = (row, t) => {
  if (!row.schedule_enabled) return t("credentialScanning.scheduleOnDemand");
  if (row.schedule_type === "interval") return t("credentialScanning.scheduleEveryNHours", { hours: row.schedule_interval_hours });
  if (row.schedule_type === "daily") return t("credentialScanning.scheduleDaily");
  if (row.schedule_type === "weekly") return t("credentialScanning.scheduleWeekly");
  if (row.schedule_type === "cron") return row.schedule_cron || t("credentialScanning.scheduleCustomCron");
  return t("credentialScanning.scheduleScheduled");
};

const DetectionTags = ({ row }) => {
  const { t } = useTranslation();
  return (
    <div className="d-flex flex-wrap gap-1">
      {row.enable_regex && (
        <span className="detection-tag detection-tag--regex">{t("credentialScanning.detTagRegex")}</span>
      )}
      {row.enable_entropy && (
        <span className="detection-tag detection-tag--entropy">{t("credentialScanning.detTagEntropy")}</span>
      )}
      {row.enable_pam_matching && (
        <span className="detection-tag detection-tag--pam">{t("credentialScanning.detTagPamMatch")}</span>
      )}
    </div>
  );
};

function ScanConfigurations() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { height } = useWindowDimensions();
  const my_capabilities = GetCapabilities();
  const isCapable = my_capabilities[0];

  const [configs, setConfigs] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pending, setPending] = useState(true);
  const [slideChecked, setSlideChecked] = useState(false);

  const [search, setSearch] = useState("");
  const [filterBy, setFilterBy] = useState("");

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [action, setAction] = useState("delete");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [toBeDeleted, setToBeDeleted] = useState([]);
  const [colName, setColName] = useState(null);

  const [scanningIds, setScanningIds] = useState({});

  const rowsPerPage = Number(localStorage.getItem("rowsPerPage_ScanConfigs") || 10);
  const tableHeight = height - 220 + "px";

  const filterOptions = [
    { label: t("credentialScanning.filterScheduleEnabled"), value: "scheduled" },
    { label: t("credentialScanning.filterOnDemandOnly"),   value: "ondemand" },
  ];
  const actionOptions = [{ label: t("credentialScanning.actionDelete"), value: "delete" }];

  const load = useCallback(async () => {
    setPending(true);
    try {
      const { data } = await getScanConfigs();
      const list = Array.isArray(data) ? data : [];
      setConfigs(list);
      setFilteredData(list);
    } catch {
      showError(t("credentialScanning.toastFailedToLoadConfigs"));
    } finally {
      setPending(false);
      setSlideChecked(true);
    }
  }, []);

  useEffect(() => {
    dispatch(changeTitle("Discovery"));
    load();
  }, [load, dispatch]);

  const handleSearch = useCallback(
    (e) => {
      if (e) e.preventDefault();
      const q = search.toLowerCase();
      let filtered = configs;
      if (filterBy === "scheduled") filtered = configs.filter((c) => c.schedule_enabled);
      else if (filterBy === "ondemand") filtered = configs.filter((c) => !c.schedule_enabled);
      if (q) filtered = filtered.filter((c) => c.name.toLowerCase().includes(q));
      setFilteredData(filtered);
    },
    [search, filterBy, configs]
  );

  useEffect(() => {
    handleSearch();
  }, [filterBy, search, handleSearch]);

  const handleRowSelected = useCallback(({ selectedRows }) => {
    setSelectedRows(selectedRows);
  }, []);

  const handleAction = () => {
    if (action === "delete" && selectedRows.length > 0) {
      setToBeDeleted(selectedRows);
      setColName(selectedRows.map((r) => r.name));
      setOpenDeleteModal(true);
    }
  };

  const handleDeleteAction = async () => {
    try {
      await Promise.all(toBeDeleted.map((r) => deleteScanConfig(r.id)));
      showSuccess(t("credentialScanning.toastDeletedConfigs", { count: toBeDeleted.length }));
      setToggleCleared((v) => !v);
      setSelectedRows([]);
      setOpenDeleteModal(false);
      setColName(null);
      load();
    } catch {
      showError(t("credentialScanning.toastFailedToDeleteConfigs"));
    }
  };

  const handleSingleDelete = async (row) => {
    setToBeDeleted([row]);
    setColName([row.name]);
    setOpenDeleteModal(true);
  };

  const pollRef = useRef(null);

  const checkActiveScans = useCallback(async (configList) => {
    const list = configList || configs;
    if (!list.length) return;
    const active = {};
    for (const c of list) {
      try {
        const { data } = await getScanHistory({ configId: c.id, page: 1 });
        const scans = data?.results || data?.scans || (Array.isArray(data) ? data : []);
        const running = scans.find((s) => s.status === "IN_PROGRESS" || s.status === "PENDING");
        if (running) active[c.id] = "scanning";
      } catch {
        // ignore
      }
    }
    setScanningIds((prev) => {
      const next = { ...prev };
      // Clear configs that are no longer running
      for (const key of Object.keys(next)) {
        if (next[key] === "scanning" && !active[key]) delete next[key];
      }
      return { ...next, ...active };
    });
    return Object.keys(active).length > 0;
  }, [configs]);

  // Check for active scans on load and start polling if any are running
  useEffect(() => {
    if (!configs.length) return;
    const runCheck = async () => {
      const hasActive = await checkActiveScans(configs);
      if (hasActive && !pollRef.current) {
        pollRef.current = setInterval(() => checkActiveScans(configs), 10000);
      } else if (!hasActive && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    runCheck();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [configs, checkActiveScans]);

  const handleStartScan = async (row) => {
    setScanningIds((prev) => ({ ...prev, [row.id]: "starting" }));
    const { error } = await startScan(row.id);
    if (error) {
      // Backend returns `{ warning: "..." }` on a 400 when the scan has no
      // viable target (no toggles on, or enabled toggles point to zero
      // targets). Route that to a yellow warning toast instead of red error.
      if (error?.warning) {
        showWarning(error.warning);
      } else {
        showError(error?.error || t("credentialScanning.toastFailedToStartScanShort"));
      }
      setScanningIds((prev) => ({ ...prev, [row.id]: null }));
    } else {
      showSuccess(t("credentialScanning.toastScanStartedForName", { name: row.name }));
      setScanningIds((prev) => ({ ...prev, [row.id]: "scanning" }));
      // Start polling if not already running
      if (!pollRef.current) {
        pollRef.current = setInterval(() => checkActiveScans(), 10000);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        name: t("credentialScanning.cfgColName"),
        selector: (row) => row.name,
        sortable: true,
        grow: 2,
        cell: (row) => (
          <span className="fw-semibold">{row.name}</span>
        ),
      },
      {
        name: t("credentialScanning.cfgColScanNow"),
        minWidth: "110px",
        grow: 1,
        cell: (row) => {
          const hasScan = isCapable?.credential_scanning?.start_scan;
          const scanState = scanningIds[row.id];
          const isBusy = scanState === "starting" || scanState === "scanning";
          if (!hasScan) return null;
          return (
            <button
              onClick={() => !isBusy && handleStartScan(row)}
              disabled={isBusy}
              style={{
                padding: "4px 12px",
                background: isBusy ? "#fff3e0" : "#5177ff",
                color: isBusy ? "#e65100" : "#fff",
                border: isBusy ? "1px solid #ffe0b2" : "none",
                borderRadius: 5,
                fontWeight: 600,
                fontSize: 12,
                cursor: isBusy ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                whiteSpace: "nowrap",
              }}
            >
              <LuScanLine size={12} className={isBusy ? "cred-scan-spin" : ""} />
              {scanState === "starting" ? t("credentialScanning.btnStartingDots") : isBusy ? t("credentialScanning.btnScanningDots") : t("credentialScanning.btnScanNow")}
            </button>
          );
        },
      },
      {
        name: t("credentialScanning.cfgColScanScope"),
        minWidth: "120px",
        grow: 1,
        cell: (row) => (
          <span className="small" style={{ whiteSpace: "nowrap" }}>
            {row.scan_all_pam_assets ? t("credentialScanning.cfgScopeAllPamAssets") : t("credentialScanning.cfgScopeCustom")}
            {row.scan_git_repos && ` ${t("credentialScanning.cfgScopePlusGit")}`}
          </span>
        ),
      },
      {
        name: t("credentialScanning.cfgColDetection"),
        minWidth: "130px",
        grow: 2,
        cell: (row) => <DetectionTags row={row} />,
      },
      {
        name: t("credentialScanning.cfgColSchedule"),
        minWidth: "110px",
        grow: 1,
        cell: (row) => (
          <span
            className={`schedule-badge ${
              row.schedule_enabled ? "schedule-badge--active" : "schedule-badge--ondemand"
            }`}
          >
            {scheduleLabel(row, t)}
          </span>
        ),
      },
      {
        name: t("credentialScanning.cfgColCreated"),
        selector: (row) => row.created_at,
        sortable: true,
        minWidth: "110px",
        grow: 1,
        cell: (row) =>
          row.created_at
            ? new Date(row.created_at).toLocaleDateString()
            : "—",
      },
      {
        name: t("credentialScanning.colHdrAction"),
        minWidth: "120px",
        cell: (row) => {
          const hasEdit = isCapable?.credential_scanning?.edit;
          const hasScan = isCapable?.credential_scanning?.start_scan;
          const hasHistory = isCapable?.credential_scanning?.view_scan_history;
          const hasDelete = isCapable?.credential_scanning?.delete;
          const scanState = scanningIds[row.id];
          const isBusy = scanState === "starting" || scanState === "scanning";

          if (!hasEdit && !hasScan && !hasHistory && !hasDelete) return null;

          return (
            <Nav>
              <NavDropdown
                id={`scan-config-action-${row.id}`}
                title={<BsThreeDotsVertical className="text-black" />}
                menuVariant="primary"
              >
                {hasEdit && (
                  <NavDropdown.Item
                    onClick={() =>
                      navigate(
                        `/dashboard/discovery/hardcoded-credentials/configs/${row.id}/edit`,
                        { state: { config: row } }
                      )
                    }
                  >
                    <FiEdit2 className="mx-2" size={13} />
                    {t("credentialScanning.menuEdit")}
                  </NavDropdown.Item>
                )}
                {hasScan && (
                  <NavDropdown.Item
                    onClick={() => !isBusy && handleStartScan(row)}
                    disabled={isBusy}
                  >
                    <LuScanLine className={`mx-2 ${isBusy ? "cred-scan-spin" : ""}`} size={13} />
                    {scanState === "starting" ? t("credentialScanning.btnStartingEllipsis") : isBusy ? t("credentialScanning.btnScanningEllipsis") : t("credentialScanning.btnScanNow")}
                  </NavDropdown.Item>
                )}
                {hasHistory && (
                  <NavDropdown.Item
                    onClick={() =>
                      navigate(
                        `/dashboard/discovery/hardcoded-credentials/scan-history`,
                        { state: { configId: row.id } }
                      )
                    }
                  >
                    <RiHistoryLine className="mx-2" size={13} />
                    {t("credentialScanning.menuViewHistory")}
                  </NavDropdown.Item>
                )}
                {hasDelete && (
                  <NavDropdown.Item onClick={() => handleSingleDelete(row)}>
                    <RiDeleteBin5Line className="mx-2" size={13} />
                    {t("credentialScanning.menuDelete")}
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            </Nav>
          );
        },
      },
    ],
    [isCapable, scanningIds, t] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <>
      <CredScanBreadcrumb current={t("credentialScanning.scanConfigurations")} />
      {pending && configs.length === 0 ? (
        <DataTableSkeleton rowCount={7} columnCount={6} />
      ) : (
        <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
          <Fade timeout={fadedelayTime} in={slideChecked}>
            <div className="main_content_container p-3 mx-auto w-100">
              <div
                className="position-relative bg-white"
                style={{ height: tableHeight }}
              >
                <DataTable
                  title={
                    <ListHeading
                      dataTableHeadingText={t("credentialScanning.scanConfigurations")}
                      dataTableHeadingIcon={Images.NetworkAnalyzer}
                    />
                  }
                  actions={
                    <ActionComponent
                      onAdd={() => navigate("/dashboard/discovery/hardcoded-credentials/configs/new")}
                    />
                  }
                  columns={columns}
                  data={filteredData}
                  className="rdt_container"
                  progressPending={pending}
                  progressComponent={
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: "200px" }}
                    >
                      <div
                        className="la-line-scale-pulse-out-rapid la-3x"
                        style={{ color: "#5177ff" }}
                      >
                        <div className="rounded" />
                        <div className="rounded" />
                        <div className="rounded" />
                        <div className="rounded" />
                        <div className="rounded" />
                      </div>
                    </div>
                  }
                  fixedHeader
                  pagination
                  paginationPerPage={rowsPerPage}
                  paginationRowsPerPageOptions={[5, 10, 25, 50]}
                  highlightOnHover
                  selectableRows
                  selectableRowsVisibleOnly
                  selectableRowsHighlight
                  onSelectedRowsChange={handleRowSelected}
                  clearSelectedRows={toggleCleared}
                  subHeader
                  subHeaderComponent={
                    <SubHeaderComponent
                      filterBy={filterBy}
                      setFilterBy={setFilterBy}
                      filterOptions={filterOptions}
                      search={search}
                      setSearch={setSearch}
                      handleSearch={handleSearch}
                      placeholder={t("credentialScanning.scanConfigurations")}
                      pages_url={pages_url}
                      action={action}
                      setAction={setAction}
                      handleAction={handleAction}
                      actionOptions={actionOptions}
                      selectedRows={selectedRows}
                    />
                  }
                  subHeaderAlign="center"
                  paginationIconFirstPage={
                    <LazyLoadImage src={FirstPageIcon} alt="first page" />
                  }
                  paginationIconLastPage={
                    <LazyLoadImage src={LastPageIcon} alt="last page" />
                  }
                  paginationIconNext={
                    <LazyLoadImage src={NextPageIcon} alt="next" />
                  }
                  paginationIconPrevious={
                    <LazyLoadImage src={PreviousPageIcon} alt="previous" />
                  }
                />

                {colName && (
                  <CustomModal
                    open={openDeleteModal}
                    handleClose={() => {
                      setOpenDeleteModal(false);
                      setColName(null);
                    }}
                  >
                    <ShowAlert
                      setOpenAlert={setOpenDeleteModal}
                      handleAlertAction={handleDeleteAction}
                      colName={colName}
                      alertMsg={t("credentialScanning.deleteConfigAlertMsg")}
                      headingMsg={t("credentialScanning.deleteConfigHeading")}
                    />
                  </CustomModal>
                )}
              </div>
            </div>
          </Fade>
        </Slide>
      )}

    </>
  );
}

export default ScanConfigurations;
